import type { ConfigState } from '@renderer/types/configState'
import { isIdrContext } from '@renderer/utils/boothLanguages'

/**
 * Which payment rail a project's guests actually meet, so copy can name the
 * rail instead of assuming Indonesia. A booth in Mexico never shows a QRIS
 * code, and telling its operator that "QRIS stays the main option on screen"
 * describes a rail that will never appear.
 *
 * Three outcomes, because the wording has to differ for all three:
 *
 * - `qris`   The guest scans a QRIS code — Indonesia natively, and the
 *            cross-border rail that bills IDR for a nearby market.
 * - `online` Some other online rail: the operator's own gateway (Mercado
 *            Pago, ToyyibPay/DuitNow, anything the long tail wires up). The
 *            project stamp doesn't carry the brand, so copy says "QR code"
 *            and never a rail name we'd have to keep in sync per country.
 * - `none`   No online rail in this region yet. The guest redeems a voucher
 *            code, and a card terminal is the only way to take money at the
 *            booth — the opposite of what the QRIS wording implied.
 *
 * Mirrors the runtime gates in `pages/view/Payment.tsx` — `isVoucherOnlyRegion`
 * and the `activeProvider === 'midtrans'` logo check. Keep the three in sync.
 */
export type PaymentRail = 'qris' | 'online' | 'none'

type PaymentRailConfig = Pick<
  ConfigState,
  'paymentSetupTier' | 'hasCustomGateway' | 'resolvedCurrency'
>

export function paymentRailFor(config: PaymentRailConfig | null | undefined): PaymentRail {
  const tier = config?.paymentSetupTier
  const hasCustomGateway = Boolean(config?.hasCustomGateway)

  // Legacy projects predate the tier stamp. IDR context (which includes "no
  // project loaded" — operator chrome on a fresh install) is the kiosk's
  // historical QRIS default; anything else we can only call generic.
  if (!tier) return isIdrContext(config?.resolvedCurrency) ? 'qris' : 'online'

  // Indonesia is QRIS even when the operator wires their own Midtrans wallet.
  if (tier === 'native_qris') return 'qris'

  // Cross-border settles in IDR over QRIS — unless the operator wired their
  // own gateway, in which case it's their rail's QR (a Malaysian booth on
  // ToyyibPay shows DuitNow QR, not QRIS).
  if (tier === 'cross_border_qris') return hasCustomGateway ? 'online' : 'qris'

  // global_hidden | localized_in_progress
  return hasCustomGateway ? 'online' : 'none'
}
